import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { FC, useEffect, useRef } from "react";

interface InfiniteScrollLoaderProps {
  onIntersect: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  disabled?: boolean;
}

const InfiniteScrollLoader: FC<InfiniteScrollLoaderProps> = ({
  onIntersect,
  hasNextPage,
  isFetchingNextPage,
  disabled = false,
}) => {
  const loaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = loaderRef.current;

    if (!currentRef || disabled || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          onIntersect();
        }
      },
      {
        root: null,
        rootMargin: "0px 0px 200px 0px",
        threshold: 0.1,
      },
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [onIntersect, hasNextPage, isFetchingNextPage, disabled]);

  if (!hasNextPage) return null;

  return (
    <Box
      ref={loaderRef}
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
      }}
    >
      {isFetchingNextPage && <CircularProgress />}
    </Box>
  );
};

export default InfiniteScrollLoader;
